
import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d620bmkcm4ll99gupo60', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-black">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <p className="text-amber-500 text-sm font-bold tracking-wider mb-6">GET IN TOUCH</p>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
          READY FOR YOUR<br />TRANSFORMATION?
        </h2>
        <p className="text-stone-400 text-lg mb-12 max-w-2xl mx-auto">
          Walk-ins welcome, but appointments are preferred to guarantee your spot. Reach out and let's get you looking fresh.
        </p>

        <div className="flex flex-wrap justify-center gap-4 mb-16">
          <button
            onClick={() => {
              const element = document.getElementById('book');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-gradient-to-r from-amber-600 to-amber-500 text-black px-8 py-4 rounded-full text-base font-bold hover:from-amber-500 hover:to-amber-400 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg shadow-amber-500/20"
          >
            BOOK APPOINTMENT →
          </button>
          <a
            href="tel:+18644499072"
            className="border border-stone-600 text-stone-300 px-8 py-4 rounded-full text-base font-bold hover:bg-white hover:text-black hover:border-white transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-phone-line"></i>
            CALL US
          </a>
        </div>

        <div className="grid md:grid-cols-3 gap-8 text-stone-400 mb-16">
          <div>
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-amber-500/10 rounded-xl">
              <i className="ri-map-pin-line text-2xl text-amber-500"></i>
            </div>
            <p className="text-sm font-semibold text-white mb-2">ADDRESS</p>
            <p className="text-sm">50 Parkins Mill Road<br />Greenville, SC 29607</p>
          </div>
          <div>
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-amber-500/10 rounded-xl">
              <i className="ri-time-line text-2xl text-amber-500"></i>
            </div>
            <p className="text-sm font-semibold text-white mb-2">HOURS</p>
            <p className="text-sm">Mon-Sat: 9AM - 7PM<br />Sunday: 10AM - 4PM</p>
          </div>
          <div>
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-amber-500/10 rounded-xl">
              <i className="ri-phone-line text-2xl text-amber-500"></i>
            </div>
            <p className="text-sm font-semibold text-white mb-2">PHONE</p>
            <p className="text-sm">864.449.9072</p>
          </div>
        </div>

        <div className="bg-neutral-900 border border-neutral-800 p-8 rounded-2xl max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold text-white mb-6">Send Us a Message</h3>
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg bg-neutral-950 border border-neutral-700 text-white placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg bg-neutral-950 border border-neutral-700 text-white placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
            <input
              type="tel"
              name="phone"
              placeholder="Your Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-neutral-950 border border-neutral-700 text-white placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
            <textarea
              name="message"
              placeholder="Your Message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              maxLength={500}
              required
              rows={4}
              className="w-full px-4 py-3 rounded-lg bg-neutral-950 border border-neutral-700 text-white placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none text-sm"
            ></textarea>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-amber-600 to-amber-500 text-black px-6 py-3 rounded-lg text-base font-bold hover:from-amber-500 hover:to-amber-400 transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
            </button>
            {submitStatus === 'success' && (
              <p className="text-amber-500 text-sm">Message sent successfully!</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-400 text-sm">Failed to send message. Please try again.</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
